/**
 * Whether this user belongs in the client portal rather than the staff app.
 *
 * Checked on clientId as well as role because role falls back to 'sales' when
 * custom:role is missing from the token, and Cognito does sometimes issue the
 * first post-login JWT before custom attributes have propagated. Without the
 * clientId half, such a client landed in the full staff app.
 *
 * BOTH route guards must use this one function. An earlier version of this fix
 * added the clientId check to the staff guard only, which turned that
 * escalation into an infinite redirect: the staff guard sent the user to the
 * portal on clientId, and the portal guard sent them back on role.
 *
 * Only a client carries custom:clientId — verified against the live dev pool,
 * where 24 users have it and none of them is staff — so its presence cannot
 * lock a staff member out.
 */
export function isClientUser(
  user: { role?: string; clientId?: string } | null
): boolean {
  if (!user) return false
  return user.role === 'client' || !!user.clientId
}

export function isAdminOrManager(user: { role?: string; isManager?: boolean } | null): boolean {
  if (!user) return false
  return user.role === 'admin' || user.isManager === true
}

// Whether the lead pipeline is relevant to this user at all. Leads are sales
// work: every other staff role was being shown a Leads by Status island that
// had nothing to do with their job. Admins and managers keep it because they
// oversee the pipeline rather than working it.
export function canSeeLeadPipeline(user: { role?: string; isManager?: boolean } | null): boolean {
  if (!user) return false
  return user.role === 'sales' || isAdminOrManager(user)
}
